import { useState } from "react";
import { PageShell } from "@/components/marketing/PageShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";

export default function VerifyReport() {
  const [reportId, setReportId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async (e) => {
    e.preventDefault();
    if (!reportId) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/verify-report", { report_id: reportId });
      setResult(data);
    } catch {
      setResult({ found: false });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell eyebrow="— AUTHENTICITY —" title="Verify a report">
      <div className="rounded-3xl border border-gray-200 bg-white p-6 sm:p-8" data-testid="verify-panel">
        <p className="max-w-2xl text-oath-secondary">
          Every ResidenceProof assurance report carries a unique report ID and a stored
          cryptographic hash. Enter a report ID to confirm the report is authentic
          and unaltered. This page mirrors the QR code printed on each signed report.
        </p>
        <form className="mt-8 flex max-w-xl flex-col gap-3 sm:flex-row" onSubmit={check}>
          <Input
            data-testid="verify-report-id"
            value={reportId}
            onChange={(e) => setReportId(e.target.value)}
            placeholder="RP-RPT-2291-0044"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="verify-submit"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-oath-ink px-6 py-3 font-mono-label text-[11px] font-semibold text-white hover:bg-black disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "VERIFY"}
          </button>
        </form>

        {result && (
          <div className="mt-8" data-testid="verify-result">
            {result.found ? (
              <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-700">
                <ShieldCheck className="h-6 w-6" />
                <div>
                  <p className="font-display font-bold">Authentic report</p>
                  <p className="text-sm">This report ID matches a signed, unaltered ResidenceProof report.</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-700">
                <ShieldAlert className="h-6 w-6" />
                <div>
                  <p className="font-display font-bold">No matching report</p>
                  <p className="text-sm">
                    We could not find a report with that ID. Signed reports are issued in Phase 06;
                    sample report IDs will be available then.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </PageShell>
  );
}
